import { useState, useEffect } from 'react';
import { Button } from '../components/Button';
import { useDispatch, useSelector } from 'react-redux';
import { updateRegistrationData, setUserData } from '../store/userSlice';
import LocationSelect from '../components/LocationSelect';
import DatePicker from '../components/DatePicker';
import { setAuthData } from '../store/authSlice';
import { axiosPublic } from '../axios';
import config from '../config';
import { toast } from 'react-toastify';

function Step1({ setStep }) {
  const dispatch = useDispatch();
  const registrationData = useSelector((state) => state.user.registrationData);
  const [disabled, setDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const genders = [
    { id: 'male', label: 'Мужчина', emoji: '👱🏻‍♂️' },
    { id: 'female', label: 'Женщина', emoji: '👱🏻‍♀️' }
  ];

  useEffect(() => {
    const isNameValid = registrationData.name?.trim()?.length > 0;
    const isGenderValid = registrationData.gender && ['male', 'female'].includes(registrationData.gender);

    const isBirthDayValid = registrationData.birthDay && new Date(registrationData.birthDay) < new Date();
    const isCountryValid = registrationData.country?.trim()?.length > 0;
    const isCityValid = registrationData.city?.trim()?.length > 0;

    setDisabled(!(isNameValid && isGenderValid && isBirthDayValid && isCountryValid && isCityValid));
  }, [registrationData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name' && value.length > 40) return;
    dispatch(updateRegistrationData({ [name]: value }));
  };

  const handleLocationSelect = (locationData) => {
    dispatch(
      updateRegistrationData({
        country: locationData.country,
        city: locationData.city,
        coordinates: locationData.coordinates
      })
    );
  };

  const handleDateChange = (date) => {
    dispatch(updateRegistrationData({ birthDay: date }));
  };

  const handleInputFocus = (e) => {
    // Сохраняем текущую позицию прокрутки
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    // Возвращаем позицию прокрутки после небольшой задержки
    setTimeout(() => {
      window.scrollTo(scrollX, scrollY);
    }, 0);
  };

  const handleNext = async () => {
    // Получаем Telegram ID из Telegram WebApp SDK
    const tg = window.Telegram?.WebApp;
    const telegramId = tg?.initDataUnsafe?.user?.id || 694603801;

    if (!telegramId) {
      toast.error('Telegram ID не найден. Откройте приложение через Telegram.');
      console.error('Telegram WebApp data:', tg?.initDataUnsafe);
      return;
    }

    console.log('Telegram ID из WebApp SDK:', telegramId);

    setIsLoading(true);
    try {
      // Подготавливаем данные для регистрации
      const registerData = {
        telegramId: telegramId,
        name: registrationData.name,
        gender: registrationData.gender,
        birthDay: registrationData.birthDay,
        country: registrationData.country,
        city: registrationData.city,
        longitude: registrationData.coordinates?.longitude || 0,
        latitude: registrationData.coordinates?.latitude || 0
      };

      console.log('Отправка данных регистрации:', registerData);

      // Отправляем запрос на регистрацию
      const response = await axiosPublic.post(config.API.AUTH.REGISTER, registerData);

      console.log('Ответ от сервера:', response.data);

      // Сохраняем токен и данные пользователя
      if (response.data.token && response.data.user) {
        console.log('📦 Данные для сохранения:');
        console.log('  - Token:', response.data.token.substring(0, 30) + '...');
        console.log('  - User ID:', response.data.user._id);
        console.log('  - Telegram ID:', response.data.user.telegramId);

        // Сохраняем токен в Redux store и localStorage
        dispatch(
          setAuthData({
            auth_token: response.data.token,
            userId: response.data.user._id,
            telegramId: response.data.user.telegramId
          })
        );
        dispatch(setUserData(response.data.user));

        setStep(2);
      } else {
        console.error('❌ Токен или данные пользователя отсутствуют в ответе');
        toast.error('Ошибка: токен не получен');
      }
    } catch (error) {
      console.error('Ошибка регистрации:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Ошибка при регистрации');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full h-full px-4">
      <h1 className="font-raleway font-semibold mt-6 text-white text-[20px]">Как вас зовут?</h1>
      <input
        type="text"
        name="name"
        placeholder="Ваше имя"
        maxLength={40}
        className="w-full h-[64px] rounded-[8px] bg-[#022424] mt-4 px-4 border border-[#233636] text-white outline-none focus:border-[#a1f69e]"
        style={{ fontSize: '16px' }}
        value={registrationData.name || ''}
        onChange={handleChange}
        onFocus={handleInputFocus}
        autoComplete="off"
        autoCapitalize="words"
        spellCheck="false"
      />
      <h1 className="font-raleway font-semibold mt-[32px] text-white text-[20px]">Ваш пол</h1>
      <div className="flex justify-start flex-wrap items-center gap-[16px] mt-[16px]">
        {genders.map((gender) => (
          <div
            key={gender.id}
            onClick={() => dispatch(updateRegistrationData({ gender: gender.id }))}
            className={`w-[145px] h-[48px] rounded-[400px] flex justify-center items-center text-[18px] text-white gap-[8px] cursor-pointer transition-all 
              ${
                registrationData.gender === gender.id
                  ? 'bg-[#043939] border-[1.5px] border-[#a1f69e]'
                  : 'bg-[#022424] border border-transparent'
              }`}
          >
            <span className="text-[24px]">{gender.emoji}</span>
            <span>{gender.label}</span>
          </div>
        ))}
      </div>

      <h1 className="font-raleway font-semibold mt-[32px] text-white text-[20px]">Ваша дата рождения?</h1>
      <div className="w-full h-[64px] flex justify-center items-center rounded-[8px] bg-[#022424] mt-4 pl-4 border border-[#233636] text-white outline-none focus:border-[#a1f69e]">
        <DatePicker value={''} onChange={handleDateChange} />
      </div>

      <LocationSelect onLocationSelect={handleLocationSelect} />

      <Button className={'mt-[37px] mb-[20px] w-full'} onClick={handleNext} disabled={disabled || isLoading}>
        {isLoading ? 'Регистрация...' : 'Далее'}
      </Button>
    </div>
  );
}

export default Step1;
